// FlagsSelect.tsx
import { FC, useState, useRef, useLayoutEffect } from 'react';
import {
  ButtonBase,
  Paper,
  MenuList,
  MenuItem,
  Popper,
  Grow,
  ClickAwayListener,
  Portal,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { redesignTheme } from '../../pages/Redesign/theme';

import FrSvg from './Flags/FR.svg';

export const CUSTOM_FLAGS: Partial<Record<string, string>> = {
  FR: FrSvg,
};

const FlagImg: FC<{ countryCode: string; size?: number }> = ({
  countryCode,
  size = 24,
}) => {
  const src =
    CUSTOM_FLAGS[countryCode.toUpperCase()] ??
    `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;

  return (
    <img
      src={src}
      width={size}
      height={size * 0.75}
      alt={countryCode}
      style={{ display: 'block', objectFit: 'cover', borderRadius: 2 }}
    />
  );
};

// ─── Country display names ─────────────────────────────────────────────────
const COUNTRY_LABELS: Record<string, string> = {
  FR: 'FR',
  GB: 'EN',
};

// ─── Style keys (exported so consumers can type their overrides) ───────────
export type FlagsSelectClassKey =
  | 'button'
  | 'buttonBlock'
  | 'label'
  | 'arrow'
  | 'popper'
  | 'popperFlagOnly'
  | 'paperFullWidth'
  | 'menuItem'
  | 'menuItemFlagOnly';

const useStyles = makeStyles((theme) =>
  createStyles({
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    buttonBlock: {
      display: 'flex',
      width: '100%',
    },
    label: {
      fontFamily: redesignTheme.fonts.opensansSemiBold,
      fontSize: '0.875rem',
    },
    arrow: {
      fontSize: '1.2rem',
      color: theme.palette.text.secondary,
    },
    popper: {
      zIndex: theme.zIndex.modal,
      minWidth: 120,
    },
    popperFlagOnly: {
      minWidth: 0,
    },
    paperFullWidth: {
      width: '100%',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
    },
    menuItemFlagOnly: {
      justifyContent: 'center',
      gap: 0,
    },
  })
);

// ─── Props ─────────────────────────────────────────────────────────────────
export interface FlagsSelectProps {
  countries: string[];
  selected: string;
  onSelect: (countryCode: string) => void;
  showSelectedLabel?: boolean;
  showOptionLabel?: boolean;
  fullWidth?: boolean;
  customLabels?: Record<string, string>;
  /** Override internal styles — same pattern as MUI's classes prop */
  classes?: Partial<Record<FlagsSelectClassKey, string>>;
  /** When false (default), the dropdown is portaled to escape overflow clipping. */
  disablePortal?: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────
const FlagsSelect: FC<FlagsSelectProps> = ({
  countries,
  selected,
  onSelect,
  showSelectedLabel = true,
  showOptionLabel = true,
  fullWidth = true,
  customLabels = {},
  classes: overrideClasses = {},
  disablePortal = false,
}) => {
  const defaultClasses = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorWidth, setAnchorWidth] = useState<number | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open || showOptionLabel) {
      setAnchorWidth(null);
      return;
    }
    const el = anchorRef.current;
    if (!el) return;
    const sync = () => setAnchorWidth(el.offsetWidth);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, showOptionLabel]);

  const cx = (key: FlagsSelectClassKey) =>
    overrideClasses[key] ?? defaultClasses[key];

  const getLabel = (code: string) =>
    customLabels[code] ?? COUNTRY_LABELS[code] ?? code;

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);
  const handleSelect = (code: string) => { onSelect(code); handleClose(); };

  const popper = (
    <Popper
      open={open}
      anchorEl={anchorRef.current}
      className={`${cx('popper')}${
        !showOptionLabel ? ` ${cx('popperFlagOnly')}` : ''
      }`}
      style={
        !showOptionLabel && anchorWidth != null
          ? { width: anchorWidth }
          : undefined
      }
      transition={true}
      placement="bottom-start"
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ width: '100%' }}>
          <Paper
            elevation={3}
            className={!showOptionLabel ? cx('paperFullWidth') : undefined}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList role="listbox" dense={true}>
                {countries.map((code) => (
                  <MenuItem
                    key={code}
                    selected={code === selected}
                    onClick={() => handleSelect(code)}
                    className={`${cx('menuItem')}${
                      !showOptionLabel ? ` ${cx('menuItemFlagOnly')}` : ''
                    }`}
                  >
                    <FlagImg countryCode={code} />
                    {showOptionLabel && (
                      <span className={cx('label')} data-wg-notranslate={true}>{getLabel(code)}</span>
                    )}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={handleToggle}
        className={`${cx('button')} ${fullWidth ? cx('buttonBlock') : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <FlagImg countryCode={selected} />
        {showSelectedLabel && (
          <span className={cx('label')} data-wg-notranslate={true}>{getLabel(selected)}</span>
        )}
        <KeyboardArrowDownIcon className={cx('arrow')} />
      </ButtonBase>

      {disablePortal ? popper : <Portal>{popper}</Portal>}
    </>
  );
};

export default FlagsSelect;